import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { useBackofficeSearchCondition } from '@/providers/BackofficeSearchConditionProvider';
import { useInvoiceTasks } from '@/hooks/api/1800_invoice_tasks';
import { useEffect, useState } from 'react';
import { dateFormat } from '@/utils';
import { Link, useNavigate } from 'react-router-dom';
import { Pagination } from '@/components/element/Pagination';
import { MotionDiv } from '@/components/element/MotionDiv';
import { FilterMatchMode } from 'primereact/api';
import { InvoiceTask, SalesOrderContractTask } from '@/hooks/api/models';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useDataTableSortContent } from '@/hooks/useDataTableSortContent';

export const ListElement = () => {
  const navigate = useNavigate();
  const { setShowLoading } = useLoading();
  const { _w_ } = useCurrentWorkspace();
  const { invoiceTaskSearchCondition: searchCondition, setInvoiceTaskSearchCondition: setSearchCondition } =
    useBackofficeSearchCondition();
  const {
    data: tasksRes,
    isPending,
    refetch,
  } = useInvoiceTasks({
    ...searchCondition,
    per: 1000,
    enabled: false,
    identifier: _w_!.identifier,
  });
  const onPageLinkClick = (_page: number) => setSearchCondition((_) => ({ ...searchCondition, page: _page }));

  useEffect(() => {
    refetch();
  }, [searchCondition]);

  useEffect(() => setShowLoading(isPending), [isPending]);

  const [filters, setFilters] = useState({
    global: { value: '', matchMode: FilterMatchMode.CONTAINS },
  });

  const phaseTemplate = (t: InvoiceTask) => t.invoice_task_phase.label;

  const actualSalesTemplate = (t: InvoiceTask) =>
    t.billing_amount_including_tax != null ? `¥${t.billing_amount_including_tax.toLocaleString()}` : '-';

  const assignTemplate = (t: InvoiceTask) => (
    <div className="d-flex align-items-center">
      <img
        src={t.user.user_account.icon_file_url}
        className="avatar avatar-xs me-2 rounded-circle"
        alt="avatar image"
      />
      <h6 className="mb-0 text-xxs">{t.user.user_account.name}</h6>
    </div>
  );

  const dueDateTemplate = (t: InvoiceTask) => (t.due_date ? dateFormat({ date: t.due_date }) : '-');

  // DataTableのソート、キーワード検索
  const { dataTableSortContent, onSort, onKeywordChanged } = useDataTableSortContent(`invoice_task_${_w_!.id}`);

  // 検索ワードの設定
  useEffect(() => {
    let _filters = { ...filters };
    _filters.global.value = dataTableSortContent.dataTableSearchKeyword?.keyword || '';
    setFilters(_filters);
  }, [dataTableSortContent]);

  if (tasksRes == null) return <></>;

  return (
    <MotionDiv className="card mb-4">
      <div className="card-header pb-0 d-flex align-items-center justify-content-between">
        <div>
          <h5 className="mb-0">請求タスク一覧</h5>
        </div>
        <div>
          <div className="input-group">
            <span className="input-group-text">
              <i className="fas fa-search"></i>
            </span>
            <input
              className="form-control"
              placeholder="Search"
              type="text"
              value={filters.global.value}
              onChange={(e) => {
                const value = e.target.value;
                let _filters = { ...filters };
                _filters.global.value = value;
                setFilters(_filters);
                onKeywordChanged(value);
              }}
            />
          </div>
        </div>
      </div>
      <div className="card-body">
        {tasksRes.invoice_tasks.length == 0 && (
          <p className="card-description text-sm">登録されている請求タスクがありません。</p>
        )}

        {tasksRes.invoice_tasks.length > 0 && (
          <div className="table-responsive">
            <DataTable
              value={tasksRes.invoice_tasks}
              stripedRows
              scrollable
              scrollHeight="800px"
              tableClassName="table data-table align-items-center clickable-row"
              removableSort
              onRowClick={(e) => {
                navigate(
                  `/w/${_w_!.identifier}/backoffice/teams/${e.data.sales_order.team_outline.id}/sales_orders/${
                    e.data.sales_order.id
                  }/invoice_tasks/${e.data.id}`
                );
              }}
              filters={filters}
              globalFilterFields={[
                'name',
                'sales_order.name',
                'billing_schedule_detail.billing_schedule_outline.year_month',
                'invoice_task_phase.label',
                'user.user_account.name',
                'sales_order.business_partner.display_name',
                'sales_order.team_outline.name',
              ]}
              emptyMessage="No Data."
              onSort={onSort}
              sortField={dataTableSortContent.dataTableSortField?.filed}
              sortOrder={dataTableSortContent.dataTableSortField?.sortOrder}
            >
              <Column
                header="取引先"
                field="sales_order.business_partner.display_name"
                bodyClassName="text-xxs px-2 font-weight-bold"
                headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                headerStyle={{ backgroundColor: '#f9fafb' }}
                bodyStyle={{
                  backgroundColor: 'inherit',
                  minWidth: '100px',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                }}
                frozen
                sortable
              ></Column>
              <Column
                header="タスク名"
                field="name"
                bodyClassName="text-xxs px-2 font-weight-bold"
                headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                headerStyle={{ backgroundColor: '#f9fafb' }}
                bodyStyle={{
                  backgroundColor: 'inherit',
                  minWidth: '250px',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                }}
                frozen
                sortable
              ></Column>
              <Column
                header="プロジェクト名"
                field="sales_order.name"
                bodyClassName="text-xxs px-2 font-weight-bold"
                headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                sortable
              ></Column>
              <Column
                header="請求月"
                field="billing_schedule_detail.billing_schedule_outline.year_month"
                bodyClassName="text-xxs px-2 font-weight-bold"
                headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                sortable
              ></Column>
              <Column
                header="フェーズ"
                field="invoice_task_phase.id"
                body={phaseTemplate}
                bodyClassName="text-xxs px-2 font-weight-bold"
                headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                sortable
              ></Column>
              <Column
                header="請求金額"
                field="billing_amount"
                body={actualSalesTemplate}
                bodyClassName="text-xxs px-2 font-weight-bold text-end pe-4"
                headerClassName="text-uppercase text-xxs font-weight-bolder text-end"
                style={{ width: '50px' }}
                sortable
              ></Column>
              <Column
                header="タスク担当者"
                field="user.user_account.name"
                body={assignTemplate}
                bodyClassName="text-xxs px-2"
                headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                sortable
              ></Column>
              <Column
                header="チーム名"
                field="sales_order.team_outline.name"
                bodyClassName="text-xxs px-2 font-weight-bold"
                headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                style={{ width: '50px' }}
                sortable
              ></Column>
              <Column
                header="売上カテゴリ"
                field="sales_order.product.name"
                bodyClassName="text-xxs px-2 font-weight-bold"
                headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                style={{ width: '50px' }}
                sortable
              ></Column>
              <Column
                header="期限日"
                field="due_date"
                body={dueDateTemplate}
                bodyClassName="text-xxs px-2 font-weight-bold"
                headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                sortable
              ></Column>
            </DataTable>
          </div>
        )}
      </div>
    </MotionDiv>
  );
};
